import 'moment/locale/ja';
import { Button, Input, Pagination, Select, Spin, Table } from 'antd';
import { useMemo, useState } from 'react';
import searchIcon from 'src/assets/images/search-icon.png';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { pageSizeList } from 'src/assets/constants';
import TableNoteNoData from 'src/components/common/tableNoteNoData';
import IconAdd from 'src/assets/icon/IconAdd';
import moment from 'moment';
import { getIndexTable } from 'src/utils';
import { useListTemplate } from 'src/api/useListTemplate';
import { decodeUrl } from 'src/utils/decodeUrl';
import ReactImageFallback from 'react-image-fallback';
import noImage from 'src/assets/images/no-image.png';
import { numberWithCommas } from 'src/utils/price';
import useItem from 'src/hook/useItem';

interface DataType {
  key: string;
  name: string;
  location: string;
  updated_at: string;
}

const columns: ColumnsType<DataType> = [
  {
    title: '番号',
    key: 'index',
    dataIndex: 'index',
    width: 100,
  },
  {
    title: '画像のサムネイル',
    dataIndex: 'image_url',
    key: 'image_url',
    width: 180,
  },
  {
    title: 'テンプレート名',
    key: 'name',
    dataIndex: 'name',
    width: 226,
  },
  {
    title: '作成日時',
    key: 'created_at',
    dataIndex: 'created_at',
    width: 150,
  },
  {
    title: '香水ラベル種類',
    dataIndex: 'acrylic',
    key: 'acrylic',
    width: 200,
  },
  {
    title: 'サイズ',
    dataIndex: 'size',
    key: 'size',
    width: 150,
  },
  {
    title: '単価  (円)',
    key: 'price',
    dataIndex: 'price',
    width: 130,
    align: 'right',
  },
  {
    title: '配送料  (円)',
    key: 'shipping_fee',
    dataIndex: 'shipping_fee',
    width: 180,
    align: 'right',
  },
  {
    title: '',
    key: '',
    dataIndex: '',
    width: 20,
  },
];

const TemplatesListComponent = () => {
  const history = useNavigate();
  const [keyword, setKeyword] = useState<string>('');
  const [keywordSearch, setKeywordSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [sizePage, setSizePage] = useState<number>(10);
  const { clearItems } = useItem();

  const { data: templateListData, isLoading } = useListTemplate(keywordSearch, page, sizePage);

  const dataSource = useMemo(() => {
    if (templateListData) {
      return templateListData?.records?.data?.map((template: any, index: any) => ({
        ...template,
        key: template?.id,
        index: <span className="line-1">{getIndexTable(page, sizePage, index)}</span>,
        image_url: (
          <span className="inline-block">
            <ReactImageFallback
              src={decodeUrl(template?.thumbnail_url)}
              fallbackImage={noImage}
              initialImage={<Spin />}
              alt=""
              className="image-in-table-acrylic-plate image-in-table-template"
            />
          </span>
        ),
        name: (
          <span
            onClick={() => handleGoToTemplateDetail(template?.id)}
            className="line-1 break-all column-redirect cursor-pointer"
            style={{ display: '-webkit-inline-box' }}
          >
            {template?.name}
          </span>
        ),
        created_at: (
          <span className="line-1 break-all">
            {template?.created_at ? moment(template?.created_at)?.utc()?.format('YYYY/MM/DD') : ''}
          </span>
        ),
        acrylic: <span className="line-1">{template?.acrylic_plate_size?.acrylic_plate?.title}</span>,
        size: <span className="line-1">{template?.acrylic_plate_size?.size_title}</span>,
        price: <span className="line-1">{numberWithCommas(template?.price)} 円</span>,
        shipping_fee: <span className="line-1">{numberWithCommas(template?.shipping_fee)} 円</span>,
      }));
    }
    return [];
  }, [templateListData]);

  const handleChangeKeyword = (value: string) => {
    setKeyword(value);
  };

  const iconSearch = () => {
    return (
      <div className="p-[5px] cursor-pointer" onClick={handleSearch}>
        <img src={searchIcon} alt="" />
      </div>
    );
  };

  const handleChangeSizePage = (value: number) => {
    setPage(1);
    setSizePage(value);
  };

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const handleSearch = () => {
    setPage(1);
    setKeywordSearch(keyword?.trim());
    setKeyword(keyword?.trim());
  };

  const handleGoToCreatePage = () => {
    history('/product/templates/create/info-general');
    clearItems();
  };

  const handleGoToTemplateDetail = (id: number | undefined) => {
    history(`/product/templates/detail/${id}`);
  };

  return (
    <div className="page-content company-page">
      <div className="mb-[16px]">
        <h2 className="page-content-title border-bottom-title">テンプレート一覧</h2>
      </div>
      <div className="page-content-table">
        <div className="flex justify-between items-center p-[16px]">
          <div className="flex items-center">
            <Input
              onPressEnter={handleSearch}
              className="input-search mr-[8px] min-w-[423px]"
              placeholder="テンプレート名を検索してください"
              suffix={iconSearch()}
              onChange={(e) => handleChangeKeyword(e.target.value)}
              value={keyword}
            />
          </div>
          <Button className="button-add max-h-[40px] w-[128px]" onClick={handleGoToCreatePage}>
            <IconAdd className="mr-[10px]" />
            追加
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          loading={isLoading}
          locale={{
            emptyText: <TableNoteNoData />,
          }}
        />

        <div className="py-[20px] px-[12px] flex justify-between items-center">
          <div className="pagination-info">
            {templateListData?.limitFrom} - {templateListData?.limitTo} 件表示/全 {templateListData?.totalRow} 件中
          </div>
          <div className="flex">
            <Pagination
              current={page}
              total={templateListData?.totalRow}
              className="pagination-main mr-[20px]"
              showSizeChanger={false}
              pageSize={sizePage}
              onChange={(page) => handleChangePage(page)}
            />
            <Select
              defaultValue={10}
              style={{ width: 153 }}
              className="pagination-options"
              onChange={(e) => handleChangeSizePage(e)}
              options={pageSizeList}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatesListComponent;
